import {
    EvaluatedCandidate,
    IEvaluator,
    PlanningContext,
    PlanningState,
    ZocNode
} from "@/core/battle/ai/types/ai.types";
import { PathInfo } from "@/core/types/battle.types";
import { getUnitTotalHp } from "@/core/utils/battle.utils";

/**
 * Movement Evaluator
 * 
 * Evaluates movement candidates based on:
 * 1. Progress toward enemy (closing distance)
 * 2. Path alignment (strategic positioning)
 * 3. Risk factors (threat, opportunity attacks)
 * 4. Stamina efficiency (move cost)
 */
export class MovementEvaluator implements IEvaluator<PathInfo> {
    
    evaluate(
        _state: PlanningState,
        ctx: PlanningContext,
        target: PathInfo
    ): EvaluatedCandidate {
        if (!target || !target.lastStep?.id) return { utility: 0 };

        const hexId = target.lastStep.id;
        const tacticalNode = ctx.tacticalMap.nodes[hexId];

        // Якщо гекс не веде до цілі, даємо мінімальний шанс (майже 0)
        if (!tacticalNode) return { utility: 0.00001 };

        // 1. ГОЛОВНА МЕТА: Безпека (0.0 або 1.0, або проміжні значення ZoC)
        const zocRisk = this.getPathRisk(target, ctx.zocMap, getUnitTotalHp(ctx.unit));
        const accessibility = 1 - zocRisk;

        // 2. "ШУМ" ДЛЯ ПРУНІНГУ: Базується на вартості ходу
        const stats = ctx.tacticalMap.stats;
        const unit = ctx.unit;
        const isReposition = ctx.goal?.type === 'REPOSITION';
        const optimalDist = unit.skills[0]?.range || 1;

        // ТВОЯ ЛОГІКА: Штраф за порушення дистанції
        const penalty = (!isReposition && optimalDist > 1 && tacticalNode.dist < optimalDist) 
            ? (optimalDist - tacticalNode.dist) / optimalDist 
            : 0;

        // СПРОЩЕНИЙ ПРОГРЕС: Чим менше moveCost, тим краще.
        // Використовуємо великий дільник (напр. 100), щоб просто отримати значення в межах 0..1
        const progress = (1 - (tacticalNode.moveCost / 100)) - penalty;

        // Фінальний мікро-шум
        const progressNoise = Math.max(0, progress) * 0.0001;
        
        // const proximity = Math.max(0, 1 - (tacticalNode.moveCost - stats.minDist) / (stats.maxDist - stats.minDist) - penalty);

        // Фінальна утиліта: наприклад, 0.99991 (безпечно і близько) або 0.99981 (безпечно, але далі)
        const utility = accessibility + progressNoise;
// const components = { accessibilty: accessibilty, total: accessibilty };
        return { 
            utility: Math.max(0, utility), 
            components: { 
                accessibility: accessibility, 
                total: utility
            } 
        };
    }

    

    private getPathRisk(pathInfo: PathInfo, zocMap: Map<string, ZocNode>, unitTotalHp: number): number {
        const path = pathInfo.path;
        if (path.length < 2) return 0; // Якщо шляху немає або це всього один гекс (ми на місці)

        let totalEntanglement = 0;
        const BASE_ZOC_PENALTY = 0.85; 
        const STEP_FATIGUE = 0.1; // Кожен наступний гекс під ZoC дається важче

        // Йдемо по точках "виходу". Остання точка в шляху — це фініш, 
        // ми з неї не виходимо, тому цикл до length - 1.
        for (let i = 0; i < path.length - 1; i++) {
            const fromHexId = path[i]!;
            const zocNode = zocMap.get(fromHexId);

            if (zocNode) {
                // 1. Штраф за кількість ворогів (контроль зони)
                const crowdFactor = zocNode.attackersCount * BASE_ZOC_PENALTY;
                
                // 2. Штраф за силу ворогів (використовуємо HP/CV як дільник)
                const threatFactor = zocNode.totalThreat / unitTotalHp;

                // Накопичуємо ризик. i — це номер кроку. 
                // Якщо i=0 і ми вже в ZoC, ми отримуємо штраф за найперший крок.
                totalEntanglement += (crowdFactor + threatFactor) * (1 + i * STEP_FATIGUE);
            }
        }

        // Нормалізація 0..1
        return Math.min(totalEntanglement, 1.0);
    }
}